import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, createTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import LogoutIcon from '@mui/icons-material/Logout';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { BsGrid3X3GapFill } from "react-icons/bs";
import { HiUsers } from 'react-icons/hi2';
import { FaBasketShopping, FaFilePrescription } from "react-icons/fa6";
import Chart from 'chart.js/auto';
import { getAllRetrieveProperties } from '../Api';
import { BiSolidCoupon } from 'react-icons/bi';
import { FaFileAlt, FaLocationArrow } from 'react-icons/fa';

// Navigation menu
const NAVIGATION = [
    {
        kind: 'header',
        title: 'Main items',
    },
    {
        segment: 'dashboard',
        title: 'Dashboard',
        icon: <DashboardIcon />,
    },
    {
        segment: 'dashboard-products',
        title: 'Products',
        icon: <FaBasketShopping style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-users',
        title: 'Users',
        icon: <HiUsers style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-categories',
        title: 'Categories',
        icon: <BsGrid3X3GapFill style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-orders',
        title: 'Orders',
        icon: <ShoppingCartIcon />,
    },
    {
        segment: 'prescriptions',
        title: 'Prescriptions',
        icon: <FaFilePrescription style={{ fontSize: '20px' }} />,
    },
    {
        segment: 'dashboard-coupons',
        title: 'Coupons',
        icon: <BiSolidCoupon style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-content',
        title: 'Content',
        icon: <FaFileAlt style={{ fontSize: '20px' }} />,
    },
    {
        segment: 'dashboard-location',
        title: 'Location',
        icon: <FaLocationArrow />,
    },
    {
        kind: 'divider',
    },
    {
        kind: 'header',
        title: 'Analytics',
    },
    {
        segment: 'reports',
        title: 'Reports',
        icon: <BarChartIcon />,
        children: [
            {
                segment: 'sales',
                title: 'Sales',
                icon: <DescriptionIcon />,
            },
            {
                segment: 'traffic',
                title: 'Traffic',
                icon: <DescriptionIcon />,
            },
        ],
    },
    {
        segment: 'integrations',
        title: 'Integrations',
        icon: <LayersIcon />,
    },
];

// Skeleton loader
const Skeleton = styled('div')(({ theme, height }) => ({
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    height,
    content: '" "',
}));

function useDemoRouter(initialPath) {
    const [pathname, setPathname] = React.useState(initialPath);
    const navigate = useNavigate();

    const router = React.useMemo(() => ({
        pathname,
        searchParams: new URLSearchParams(),
        navigate: (path) => {
            setPathname(String(path));
            navigate(path);
        },
    }), [pathname, navigate]);

    return router;
}

const theme = createTheme({ palette: { mode: 'light' } });

function TrafficReport({ onLogout }) {
    const navigate = useNavigate();
    const router = useDemoRouter('/reports/traffic');
    const [loading, setLoading] = useState(false);
    const [trafficData, setTrafficData] = useState([]);
    const [error, setError] = useState(null);
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        // No date gate any more: this endpoint has no date dimension, so requiring
        // a range only ever blocked the report on a condition that did not affect
        // its output. See the comment on the form below.
        setLoading(true);
        setError(null);
        try {
            const data = await getAllRetrieveProperties('/customers/totals');
            setTrafficData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (trafficData.length > 0 && chartRef.current) {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
            const ctx = chartRef.current.getContext('2d');
            chartInstanceRef.current = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: trafficData.map(item => item.name),
                    datasets: [
                        {
                            label: 'Total Customers',
                            data: trafficData.map(item => item.total),
                            backgroundColor: [
                                'rgba(59, 130, 246, 0.6)',
                                'rgba(34, 197, 94, 0.6)'
                            ],
                            borderColor: [
                                '#3b82f6',
                                '#22c55e'
                            ],
                            borderWidth: 1,
                        }
                    ],
                },
                options: {
                    responsive: true,
                    plugins: {
                        legend: { display: false },
                        title: { display: true, text: 'Paying vs Non-Paying Customers' },
                    },
                    scales: {
                        y: { beginAtZero: true, title: { display: true, text: 'Number of Customers' } },
                        x: { title: { display: true, text: 'Customer Type' } },
                    },
                },
            });
        }

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };
    }, [trafficData]);

    const branding = {
        logo: (
            <div className="header-logo">
                <img onClick={() => navigate('/')} src={require('../images/logo.png')} alt="" style={{ height: '52px', width: '60px' }} />
            </div>
        ),
        title: '',
    };

    const ToolbarActions = () => (
        <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={() => setOpenLogoutDialog(true)}
            sx={{ marginRight: 2 }}
            style={{ background: "#0165AB", color: "#fff" }}
        >
            Logout
        </Button>
    );

    return (
        <AppProvider navigation={NAVIGATION} router={router} theme={theme} branding={branding}>
            <DashboardLayout slots={{ toolbarActions: ToolbarActions }}>
                <PageContainer>
                    {loading ? (
                        <Grid container spacing={1}>
                            <Grid size={12}><Skeleton height={150} /></Grid>
                        </Grid>
                    ) : (
                        <section className="traffic-dashboard">
                            <Grid container spacing={2}>
                                {/* BUG-6: a start/end date picker used to sit here. It could
                                    never work: this report reads
                                    `wc/v3/reports/customers/totals`, which counts customers
                                    by type and has no date dimension at all — so WooCommerce
                                    would discard any dates we sent and the numbers would be
                                    identical whatever was picked. Removed rather than wired
                                    to a parameter the API ignores. Making this genuinely
                                    date-filtered needs a different data source, which is a
                                    reporting decision rather than a bug fix. */}
                                <Grid size={12}>
                                    <form onSubmit={handleSubmit}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 8 }}>
                                                <p style={{ margin: 0, color: '#555' }}>
                                                    Current customer totals, counted live. This report
                                                    is not date-filtered.
                                                </p>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                <Button type="submit" variant="contained" style={{ background: "#0165AB", color: "#fff" }}>
                                                    Generate Report
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </Grid>

                                {error && (
                                    <Grid size={12}>
                                        <p className="text-red-500">{error}</p>
                                    </Grid>
                                )}

                                {trafficData.length > 0 && (
                                    <>
                                        <Grid size={12}>
                                            <h2>Traffic Chart</h2>
                                            <canvas ref={chartRef}></canvas>
                                        </Grid>
                                        <Grid size={12}>
                                            <h2>Traffic Summary</h2>
                                            <p>Total Page Views: {trafficData.reduce((sum, i) => sum + parseFloat(i.page_views), 0)}</p>
                                            <p>Total Unique Visitors: {trafficData.reduce((sum, i) => sum + i.unique_visitors, 0)}</p>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </section>
                    )}
                </PageContainer>
            </DashboardLayout>

            {/* Logout dialog */}
            <Dialog open={openLogoutDialog} onClose={() => setOpenLogoutDialog(false)}>
                <DialogTitle>Confirm Logout</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to log out?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenLogoutDialog(false)}>Cancel</Button>
                    <Button onClick={() => { setOpenLogoutDialog(false); onLogout(); navigate('/'); }}>Logout</Button>
                </DialogActions>
            </Dialog>
        </AppProvider>
    );
}

export default TrafficReport;
